<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/breadcrumbs';
import collectionsDocs from 'docs:@react-stately/collections';
import {HeaderInfo, PropTable, TypeLink} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {Breadcrumbs, Item} from '@react-spectrum/breadcrumbs';
import {View} from '@react-spectrum/view';
```

---
category: Navigation
---

# Breadcrumbs

<p>{docs.exports.Breadcrumbs.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['Breadcrumbs', 'Item']}
  sourceData={[
    {type: 'Spectrum', url: 'https://spectrum.adobe.com/page/breadcrumbs/'}
  ]} />

## Example

```tsx example
<Breadcrumbs>
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
  <Item key="march 2020 assets">March 2020 Assets</Item>
</Breadcrumbs>
```

## Content
Breadcrumbs follow the [Collection Components](../react-stately/collections.html) API, accepting only static children. Similar to the Menu, Breadcrumbs accept an `<Item>` component, each with a `key` prop which is passed to the `onAction` handler to identify the selected item. Basic usage of Breadcrumbs, seen in the example above, shows a list of <TypeLink links={collectionsDocs.links} type={collectionsDocs.exports.Item} /> components, populated with a string. See [Events](#events) for more information.

### Internationalization
In order to internationalize Breadcrumbs, the strings of all child items should be localized.

## Events
Use the `onAction` prop as a callback to handle press events on items.

```tsx example
function Example() {
  let folders = [
    {id: 1, label: 'Home'},
    {id: 2, label: 'Trendy'},
    {id: 3, label: 'March 2020 Assets'}
  ];
  let [folderId, setFolderId] = React.useState();
  return (
    <div>
      <Breadcrumbs onAction={(a) => setFolderId(a)}>
        {folders.map(f => <Item key={f.id}>{f.label}</Item>)}
      </Breadcrumbs>
      <p>You pressed folder ID: {folderId}</p>
    </div>
  );
}
```

## Props

<PropTable component={docs.exports.Breadcrumbs} links={docs.links} />

## Visual options

### Size
[View guidelines](https://spectrum.adobe.com/page/breadcrumbs/#Options)

The small size is useful when available space is limited.

```tsx example
<Breadcrumbs size="S">
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
</Breadcrumbs>
```

The medium size is the default.
```tsx example
<Breadcrumbs size="M">
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
</Breadcrumbs>
```

The large size places emphasis on the selected item as a page title or heading.

```tsx example
<Breadcrumbs size="L">
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
</Breadcrumbs>
```

### Heading

Use the `isHeading` prop to add a role of "heading" to the highlighted item.
If a change to the aria-level is required, use the `headingAriaLevel` prop.

```tsx example
<Breadcrumbs isHeading headingAriaLevel={2}>
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
  <Item key="march 2020 assets">March 2020 Assets</Item>
</Breadcrumbs>
```

### Visible items (overflow behavior)
[View guidelines](https://spectrum.adobe.com/page/breadcrumbs/#Truncation-and-overflow)

Breadcrumbs collapse into a menu by default, showing only a maximum of visible 4 items.

If using `maxVisibleItems="auto"`, overflow of breadcrumb items begins when there is not enough room to display all items
in the parent container.

In order to manually control the overflow, use the `maxVisibleItems` prop.

```tsx example
<Breadcrumbs maxVisibleItems={2}>
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
  <Item key="march 2020 assets">March 2020 Assets</Item>
</Breadcrumbs>
```

### Root context
[View guidelines](https://spectrum.adobe.com/page/breadcrumbs/#With-root-context)

Some applications find that displaying the root item is useful for orienting users.
This variation keeps the root visible when other items are truncated into the menu.

```tsx example
<View overflow="hidden" width="200px">
  <Breadcrumbs showRoot maxVisibleItems="auto">
    <Item key="home">Home</Item>
    <Item key="trendy">Trendy</Item>
    <Item key="2020 assets">March 2020 Assets</Item>
    <Item key="winter">Winter</Item>
    <Item key="holiday">Holiday</Item>
  </Breadcrumbs>
</View>
```

### Disabled

Breadcrumbs in a disabled state shows that selection exists, but is not available in that circumstance.
This can be used to maintain layout continuity.

```tsx example
<Breadcrumbs isDisabled>
  <Item key="home">Home</Item>
  <Item key="trendy">Trendy</Item>
  <Item key="march 2020 assets">March 2020 Assets</Item>
</Breadcrumbs>
```
